import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TAMANHOS_PAGINA, type TamanhoPagina } from "@/hooks/usePaginacaoLocal";

interface PaginacaoRodapeProps {
  pagina: number;
  totalPaginas: number;
  totalItens: number;
  primeiroItem: number;
  ultimoItem: number;
  tamanhoPagina: TamanhoPagina;
  onPaginaChange: (p: number) => void;
  onTamanhoPaginaChange: (t: TamanhoPagina) => void;
}

/**
 * Rodapé de paginação padronizado.
 * Lista paginada no servidor: `<PaginacaoRodape {...usePaginacaoServidor().rodape(resposta)} />`.
 * Lista já inteira em memória: use junto com o hook `usePaginacaoLocal`.
 */
export function PaginacaoRodape({
  pagina,
  totalPaginas,
  totalItens,
  primeiroItem,
  ultimoItem,
  tamanhoPagina,
  onPaginaChange,
  onTamanhoPaginaChange,
}: PaginacaoRodapeProps) {
  // Páginas numeradas (janela de 5 ao redor da página atual)
  const paginas: number[] = [];
  if (totalPaginas <= 5) {
    for (let i = 1; i <= totalPaginas; i++) paginas.push(i);
  } else if (pagina <= 3) {
    paginas.push(1, 2, 3, 4, 5);
  } else if (pagina >= totalPaginas - 2) {
    for (let i = totalPaginas - 4; i <= totalPaginas; i++) paginas.push(i);
  } else {
    for (let i = pagina - 2; i <= pagina + 2; i++) paginas.push(i);
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      {/* Contador */}
      <p className="text-sm text-muted-foreground">
        {totalItens === 0
          ? "Nenhum registro"
          : `Mostrando ${primeiroItem}–${ultimoItem} de ${totalItens} registro${totalItens !== 1 ? "s" : ""}`}
      </p>

      {/* Controles */}
      <div className="flex items-center gap-4">
        {/* Itens por página */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
          <Select
            value={String(tamanhoPagina)}
            onValueChange={(v) => onTamanhoPaginaChange(Number(v) as TamanhoPagina)}
          >
            <SelectTrigger className="w-[75px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAMANHOS_PAGINA.map((s) => (
                <SelectItem key={s} value={String(s)}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Navegação */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onPaginaChange(1)}
            disabled={pagina === 1}
          >«</Button>
          <Button
            variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onPaginaChange(pagina - 1)}
            disabled={pagina === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {paginas.map((p) => (
            <Button
              key={p}
              variant={pagina === p ? "default" : "outline"}
              size="icon" className="h-8 w-8 text-xs"
              onClick={() => onPaginaChange(p)}
            >{p}</Button>
          ))}

          <Button
            variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onPaginaChange(pagina + 1)}
            disabled={pagina >= totalPaginas}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onPaginaChange(totalPaginas)}
            disabled={pagina >= totalPaginas}
          >»</Button>
        </div>

        <span className="text-sm text-muted-foreground whitespace-nowrap">
          Página {pagina} de {totalPaginas}
        </span>
      </div>
    </div>
  );
}
